import { useEffect } from "react"

import { observeNotifier } from "@pkg/sse-notifier/src"
import type { QueryClient } from "@tanstack/react-query"
import { useQueryClient } from "@tanstack/react-query"

/** NotificationEvent.ActiveLoanStatusUpdated on the proposal's shared channel. */
export const ACTIVE_LOAN_STATUS_UPDATED_EVENT = `vision_sungage_loan_status_updated`

/**
 * Refetch a proposal's Concert reads when the gateway publishes an application
 * change for it. The browser never talks to Concert; the gateway reconciles
 * and notifies. `invalidate` names the kind's reads; pass a module-level
 * function so the subscription is not rebuilt on every render.
 */
export function useConcertApplicationNotifications(
  proposalId: string | undefined,
  invalidate: (queryClient: QueryClient, proposalId: string) => void,
): void {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!proposalId) {
      return undefined
    }
    const subscription = observeNotifier({
      channelNameSpec: proposalId,
      sseUrl: `/notifier/stream`,
    }).subscribe({
      next: (event) => {
        if (
          event.type === `MESSAGE` &&
          event.message.data === ACTIVE_LOAN_STATUS_UPDATED_EVENT
        ) {
          invalidate(queryClient, proposalId)
        }
      },
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [invalidate, proposalId, queryClient])
}
